// Permission mapping for menu items
// This file centralizes all permission requirements for menu items

export const PERMISSION_MAPPING = {
  // Dashboard
  dashboard: [],
  
  // Calendar
  calendar: ['calendar.view'],
  
  // Sales Module
  sales: {
    group: ['sales.view', 'estimates.view', 'invoices.view', 'customers.view'],
    estimates: ['estimates.view'],
    invoices: ['sales.view'],
    workspaces: ['customers.view', 'sales.view', 'estimates.view'],
    customers: ['customers.view'],
  },
  
  // Services & Products (configurableTemplates/configurableGroups gated by items_advanced feature; CRUD by items.*)
  services: {
    group: ['items.view', 'categories.view'],
    items: ['items.view'],
    categories: ['categories.view'],
    configurableTemplates: ['items.view'],
    configurableGroups: ['items.view'],
  },
  
  // Inventory
  inventory: {
    group: ['inventory.view'],
    items: ['inventory.view'],
    categories: ['inventory_categories.view']
  },

  // Accounting
  accounting: {
    group: ['taxes.view', 'reports.view', 'statements.view'],
    taxes: ['taxes.view'],
    reports: ['reports.view'],
    statements: ['statements.view'],
    receivePayments: ['payments_receivable.view'],
    payBills: ['payments_payable.view'],
  },
  
  // Purchases
  purchases: {
    /*
      The group opens for any one of these. A warehouse person who only receives
      deliveries, or an approver who only ever looks at orders, still needs the
      Purchases menu to appear — gating the whole group on `bills.view` hid the
      entire module from people whose whole job is inside it.
    */
    group: [
      'suppliers.view', 'bills.view', 'purchase_orders.view',
      'goods_receipts.view', 'receipts.view',
    ],
    suppliers: ['suppliers.view'],
    bills: ['bills.view'],
    purchaseOrders: ['purchase_orders.view'],
    goodsReceipts: ['goods_receipts.view'],
    receipts: ['receipts.view'],
    /* No permission of its own: the inbox returns only what you are personally
       an approver for, so the filter IS the authorisation. */
    approvals: [],
    approvalPolicies: ['approval_policies.view'],
  },
  
  // User Management
  profile: [],
  todos: ['todos.view'],
  
  // Organization
  organization: {
    group: ['users.view', 'business_profile.view', 'organization_billing.view'],
    users: ['users.view'],
    business: ['business_profile.view'],
    billing: ['organization_billing.view'],
  },

  // Business profile sub-app — granular per-page edit permissions live here.
  business_profile: {
    view:                 ['business_profile.view'],
    brandingEdit:         ['business_profile.branding.edit'],
    businessInfoEdit:     ['business_profile.business_info.edit'],
    welcomeEdit:          ['business_profile.welcome.edit'],
  },
  
  // Admin
  admin: {
    group: ['bank_accounts.view', 'settings.view', 'organization_licenses.view'],
    bankAccounts: ['bank_accounts.view'],
    settings: ['settings.view'],
    licenses: ['organization_licenses.view'],
  },
  
  // Support - no permissions required
  support: [],
};

// Menu items that require a license feature (e.g. items_advanced) to be visible
export const FEATURE_MAPPING = {
  services: {
    configurableTemplates: ['items_advanced'],
    configurableGroups: ['items_advanced'],
  },
};

// Helper: true if user has all required license features (by key)
// licenseFeatures can be array or object keyed by feature key (API may return either)
export const checkFeatureAccess = (requiredFeatureKeys = [], licenseFeatures = []) => {
  if (!requiredFeatureKeys || requiredFeatureKeys.length === 0) return true;
  const list = Array.isArray(licenseFeatures)
    ? licenseFeatures
    : Object.values(licenseFeatures || {});
  if (list.length === 0) return false;
  const enabledKeys = list.filter(f => f?.enabled).map(f => f?.key);
  return requiredFeatureKeys.every(key => enabledKeys.includes(key));
};

// Helper: get required feature keys for a menu item (e.g. 'services.configurableTemplates' -> ['items_advanced'])
export const getFeaturesForMenuItem = (menuKey) => {
  const keys = menuKey.split('.');
  let current = FEATURE_MAPPING;
  for (const k of keys) {
    current = current?.[k];
    if (current === undefined) return [];
  }
  return Array.isArray(current) ? current : [];
};

// Module access mapping for menu items (controls visibility)
export const MODULE_ACCESS_MAPPING = {
  // Core
  dashboard: [],
  calendar: ['calendar'],

  // Sales
  sales: {
    group: ['sales'],
    estimates: ['estimates'],
    invoices: ['sales'],
    workspaces: ['sales', 'estimates', 'customers'],
    customers: ['customers'],
  },

  // Services & Products
  services: {
    group: ['items', 'categories'],
    items: ['items'],
    categories: ['categories'],
    configurableTemplates: ['items'],
    configurableGroups: ['items'],
  },

  // Inventory (map to 'inventory' module if exists)
  inventory: {
    group: ['inventory'],
    items: ['inventory'],
    categories: ['inventory'],
  },

  // Accounting
  accounting: {
    group: ['taxes', 'reports'],
    taxes: ['taxes'],
    reports: ['reports'],
    statements: ['statements'],
    receivePayments: ['payments_receivable'],
    payBills: [], // Pay Bills appears in Accounting menu even when purchases not purchased; access gated by payments_payable.*
  },

  // Purchases
  purchases: {
    group: ['purchases'],
    suppliers: ['suppliers'],
    bills: ['bills'],
    /*
      These are permission GROUPS, not license-feature keys — `module_access` is
      built from Permission.group (LicenseFeatureService::getAccessibleModules).
      There is no group called 'purchases', so naming it here matched nothing.
    */
    purchaseOrders: ['purchase_orders'],
    goodsReceipts: ['goods_receipts'],
    receipts: ['bills'],
    approvals: ['bills', 'purchase_orders'],
    approvalPolicies: ['approval_policies'],
  },

  // User Management
  profile: [],
  todos: ['todos'],

  // Organization
  organization: {
    group: ['users', 'business'],
    users: ['users'],
    business: ['business'],
    billing: [],
    roles: ['roles'],
  },

  // Admin
  admin: {
    group: ['settings', 'organization_licenses'],
    bankAccounts: ['bank_accounts'],
    settings: ['settings'],
    licenses: ['organization_licenses'],
  },

  // Reports (standalone item used below groups)
  reports: ['reports'],

  // Support (always visible)
  support: [],
};

// Helper to traverse nested mapping by key path like 'sales.estimates'
export const getModulesForMenuItem = (menuKey) => {
  const keys = menuKey.split('.');
  let current = MODULE_ACCESS_MAPPING;

  for (const key of keys) {
    if (current && current[key] !== undefined) {
      current = current[key];
    } else {
      return [];
    }
  }

  return Array.isArray(current) ? current : [];
};

// Check if user has access to at least one required module
export const checkModuleAccess = (requiredModules = [], moduleAccess = []) => {
  if (!requiredModules || requiredModules.length === 0) return true;
  if (!moduleAccess || moduleAccess.length === 0) return false;
  return requiredModules.some(m => moduleAccess.includes(m));
};

// Helper function to get permissions for a specific menu item
export const getPermissionsForMenuItem = (menuKey) => {
  const keys = menuKey.split('.');
  let current = PERMISSION_MAPPING;
  
  for (const key of keys) {
    if (current[key]) {
      current = current[key];
    } else {
      return [];
    }
  }
  
  return Array.isArray(current) ? current : [];
};

// Helper function to check if user has any permission from a list
export const hasAnyPermission = (requiredPermissions, userPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  
  return requiredPermissions.some(permission => userPermissions.includes(permission));
};

// Helper function to check if user has all permissions from a list
export const hasAllPermissions = (requiredPermissions, userPermissions) => {
  if (!requiredPermissions || requiredPermissions.length === 0) return true;
  if (!userPermissions || userPermissions.length === 0) return false;
  
  return requiredPermissions.every(permission => userPermissions.includes(permission));
};

// Main permission checking function that takes userPermissions as parameter
export const checkMenuPermissions = (requiredPermissions = [], userPermissions = []) => {

  
  if (requiredPermissions.length === 0) {

    return { hasAccess: true, isLimited: false };
  }
  
  const hasAny = hasAnyPermission(requiredPermissions, userPermissions);
  const hasAll = hasAllPermissions(requiredPermissions, userPermissions);
  
  const result = {
    // hasAccess: true, // Always show menu items
    // isLimited: !hasAll // Show limited if user doesn't have all permissions    
    hasAccess: hasAny, // Show menu items only if user has at least one required permission
    isLimited: hasAny && !hasAll // Show limited if user has some but not all permissions
  };
  
  
  return result;
}; 